import { forwardRef, type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils.js';

/* ===========================================================================
 * Badge — a status triple on a pale tint: surface, border and ink together.
 *
 * SHAPE. The sister app's two sources disagree: DESIGN_SYSTEM.md specifies a
 * 4px radius, Badge.jsx renders a full pill. We took the document — a squared
 * tag reads as a data value in a dense table, a pill reads as a consumer chip,
 * and these badges are overwhelmingly used inside tables. That reasoning still
 * holds; only the number moved. `rounded-xs` is now 8px rather than 4px, which
 * is the chip role in docs/design-rules.md §1, and it arrives here for free
 * because this component names the ROLE and not the value.
 *
 * THE BORDER IS NEW, AND IT IS THE POINT OF THIS REVISION.
 *
 * Every tinted variant used to be a two-part thing: a pale fill plus its ink.
 * That is fine on a desk and it fails in the field. Measured against a white
 * card, `success-surface` and `warning-surface` sit around 1.3:1 — on a phone
 * held at arm's length in Gulf sunlight they are the same pale rectangle, so
 * "present" and "late" become indistinguishable at exactly the moment someone
 * needs to tell them apart. docs/design-rules.md §6 names this directly: never
 * let a tint alone distinguish two states in sunlight. The border carries the
 * distinction when the fill cannot.
 *
 * So a status is now a TRIPLE — `bg-*-surface`, `border-*-border`,
 * `text-*-on-surface` — and every hue in the token layer supplies all three.
 *
 * INK. Never `text-*` (the solid fill colour) and never `text-*-foreground`
 * (the ink meant for the solid fill). `-foreground` on a tint is white-on-
 * pastel in light and, because the dark scheme puts the on-colour and the
 * container on the same tone, literally invisible in dark. The `-on-surface`
 * role exists for exactly this and clears AAA in both themes.
 *
 * TYPE. `text-label` (12.5px), not `text-badge` (11px). §1 of the rules bans
 * 11px for labels — at that size a badge stops being a word you read and
 * becomes texture you infer, which is the opposite of what a status is for.
 * `--text-badge` survives in the token layer as a frozen, deprecated step; do
 * not reach for it here.
 *
 * The WEIGHT is `font-bold`, overriding the 600 that `text-label` carries. The
 * mock draws every tag at 700 and the §1 type table lists 600 for a label; the
 * mock wins on this one because it is asking for the badge to be MORE legible,
 * not less. It is the same trade as the size — a tag has to survive being read
 * at arm's length in a table of forty of them — and 700 makes it at 12.5px
 * without borrowing the 11px that would undo it.
 * ======================================================================== */

/**
 * One tinted status. Spelling the triple out per variant rather than
 * generating it keeps every hue greppable — `border-warning-border` is
 * findable, `border-${hue}-border` is not.
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1 whitespace-nowrap rounded-xs border px-2.5 py-0.5 text-label font-bold',
  {
    variants: {
      variant: {
        // Neutral is a real triple now, not `--muted` borrowed as a fill and
        // `--muted-foreground` borrowed as ink. It reads "no state to report",
        // which in this product is a claim in its own right — a rest day is
        // not a 0% day (§3.9) and it should not be drawn in a hue that implies
        // something went either well or badly.
        default: 'bg-neutral-surface border-neutral-border text-neutral-on-surface',
        neutral: 'bg-neutral-surface border-neutral-border text-neutral-on-surface',
        // The one solid-filled variant, so it pairs the *other* way round:
        // solid fill takes the `-foreground` ink, and the border is the fill
        // itself so the geometry matches its tinted siblings exactly.
        primary: 'bg-primary border-primary text-primary-foreground',
        success: 'bg-success-surface border-success-border text-success-on-surface',
        warning: 'bg-warning-surface border-warning-border text-warning-on-surface',
        danger: 'bg-danger-surface border-danger-border text-danger-on-surface',
        info: 'bg-info-surface border-info-border text-info-on-surface',
        accent: 'bg-accent-surface border-accent-border text-accent-on-surface',
        // Untinted: the border IS the badge. Kept for the case where a value
        // needs delimiting without claiming a status.
        outline: 'bg-transparent border-border text-foreground',
      },
    },
    defaultVariants: { variant: 'default' },
  }
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => (
    <span ref={ref} className={cn(badgeVariants({ variant }), className)} {...props} />
  )
);
Badge.displayName = 'Badge';
